var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader } from '@ui';
import { Checkbox } from '@ui/molecules/inputs/Checkbox';
import { Popup, PopupSearchInput } from '@ui/molecules/Popup';
import './ExclusionMultiSelectStyles.scss';
const ASYNC_MIN_CHARS = 2;
const ASYNC_DEBOUNCE_MS = 250;
// Selected options are remembered across searches so chips survive a query that
// no longer returns them; used to render the trigger summary + keep checks lit.
function useResolvedLabels() {
    const [labels, setLabels] = useState({});
    const remember = useCallback((opts) => {
        setLabels((prev) => {
            const next = Object.assign({}, prev);
            opts.forEach((o) => {
                next[o.value] = o.label;
            });
            return next;
        });
    }, []);
    return [labels, remember];
}
/**
 * Searchable multi-select with checkbox rows for the Add Exclusion modal
 * (IP-14862). Composes QUI Popup + PopupSearchInput + Checkbox. Supports both a
 * static option list (accounts / tags, filtered client-side) and an async
 * loader (buildings). Values are strings to match ia_check_config.scope_key.
 */
export function ExclusionMultiSelect({ label, placeholder, searchPlaceholder, emptyMessage, selected, selectedOptions, onChange, options, asyncSearch }) {
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [asyncOptions, setAsyncOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [labelMap, rememberLabels] = useResolvedLabels();
    // Seed the label map from static options so the summary/chips resolve names.
    useEffect(() => {
        if (options)
            rememberLabels(options);
    }, [options, rememberLabels]);
    useEffect(() => {
        if (selectedOptions)
            rememberLabels(selectedOptions);
    }, [selectedOptions, rememberLabels]);
    // Debounced async search for the buildings axis. Empty query is allowed —
    // the Rails search endpoints return the first page when `q` is blank, so
    // opening the popup preloads results (parity with static account lists).
    useEffect(() => {
        if (!asyncSearch || !open)
            return undefined;
        const term = query.trim();
        if (term.length > 0 && term.length < ASYNC_MIN_CHARS) {
            // Keep the last preloaded/search page while the user types the 1st char;
            // don't flash empty.
            return undefined;
        }
        setLoading(true);
        let active = true;
        const handle = setTimeout(() => {
            void (() => __awaiter(this, void 0, void 0, function* () {
                try {
                    const results = yield asyncSearch(term);
                    if (!active)
                        return;
                    rememberLabels(results);
                    setAsyncOptions(results);
                }
                catch (_a) {
                    if (active)
                        setAsyncOptions([]);
                }
                finally {
                    if (active)
                        setLoading(false);
                }
            }))();
        }, term.length === 0 ? 0 : ASYNC_DEBOUNCE_MS);
        return () => {
            active = false;
            clearTimeout(handle);
        };
    }, [query, asyncSearch, rememberLabels, open]);
    const visibleOptions = useMemo(() => {
        // Selected first (always toggleable), then search/preload hits. IP-14860.
        const selectedRows = selected.map((value) => {
            var _a;
            return ({
                value,
                label: (_a = labelMap[value]) !== null && _a !== void 0 ? _a : value
            });
        });
        const selectedIds = new Set(selected);
        if (asyncSearch) {
            const rest = asyncOptions.filter((o) => !selectedIds.has(o.value));
            return [...selectedRows, ...rest];
        }
        const list = options !== null && options !== void 0 ? options : [];
        const term = query.trim().toLowerCase();
        const filtered = term ? list.filter((o) => o.label.toLowerCase().includes(term)) : list;
        const selectedVisible = term
            ? selectedRows.filter((o) => o.label.toLowerCase().includes(term))
            : selectedRows;
        const rest = filtered.filter((o) => !selectedIds.has(o.value));
        return [...selectedVisible, ...rest];
    }, [asyncSearch, asyncOptions, options, query, selected, labelMap]);
    const toggle = (value) => {
        onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
    };
    // Comma-separated names in the trigger; CSS ellipsis truncates overflow
    // (IP-14860 / IP-14862). No chip tags below the control.
    const summary = useMemo(() => {
        if (selected.length === 0)
            return placeholder;
        return selected.map((v) => { var _a; return (_a = labelMap[v]) !== null && _a !== void 0 ? _a : v; }).join(', ');
    }, [selected, labelMap, placeholder]);
    // ClickAway fires on trigger mousedown (trigger is outside the popup), which
    // would race a naive toggle and re-open. Lock on mousedown when already open
    // so ClickAway is ignored and the click handler closes — normal select UX.
    const closingViaTriggerRef = useRef(false);
    const handleTriggerMouseDown = () => {
        if (open)
            closingViaTriggerRef.current = true;
    };
    const handleTriggerClick = () => {
        if (closingViaTriggerRef.current) {
            closingViaTriggerRef.current = false;
            setOpen(false);
            return;
        }
        setOpen(true);
        setQuery('');
    };
    const handleClose = () => {
        if (closingViaTriggerRef.current)
            return;
        setOpen(false);
    };
    const showEmpty = !loading && visibleOptions.length === 0;
    const isPlaceholder = selected.length === 0;
    // Mirror the QUI labeled-select markup (qmb-ui-select / qmb-ui-select--labeled)
    // so this value picker is visually identical to the "Exclude by" SelectInput
    // directly above it: a trigger button carrying the value + a floating <label>
    // sibling, both wrapped in .qmb-ui-select. The popup reuses PopupSearchInput +
    // Checkbox rows for the multi-select behavior QUI SelectInput lacks (async +
    // remembered chips). IP-14862 QA fix.
    return (React.createElement("div", { className: "qmb-ui-select qmb-ui-select--labeled aim-exclusion-select" },
        React.createElement("button", { type: "button", ref: anchorRef, className: `qmb-ui-select__trigger${isPlaceholder ? ' qmb-ui-select__trigger--placeholder' : ''}`, "aria-label": label, "aria-haspopup": "listbox", "aria-expanded": open, onMouseDown: handleTriggerMouseDown, onClick: handleTriggerClick }, summary),
        React.createElement("label", { className: "qmb-ui-label" }, label),
        React.createElement(Popup, { anchor: anchorRef.current, show: open, onClose: handleClose, position: "bottom-left", containerClass: "qmb-ui-popup--action-list qmb-ui-select__popup aim-exclusion-select__popup" },
            React.createElement(PopupSearchInput, { value: query, onChange: setQuery, placeholder: searchPlaceholder }),
            React.createElement("div", { className: "popup__section aim-exclusion-select__options", role: "listbox", "aria-label": label },
                loading && (React.createElement("div", { className: "aim-exclusion-select__loading" },
                    React.createElement(Loader, { size: "16" }))),
                showEmpty && React.createElement("p", { className: "aim-exclusion-select__empty" }, emptyMessage),
                !loading &&
                    visibleOptions.map((opt) => (React.createElement(Checkbox, { key: opt.value, id: `aim-exclusion-opt-${opt.value}`, className: "aim-exclusion-select__row", checked: selected.includes(opt.value), onChange: () => toggle(opt.value), label: opt.label })))))));
}
export default ExclusionMultiSelect;
